/****************************************************
 * Module Name:  stoneDetailMemoOut.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Container, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";

import { styled } from "@mui/material/styles";
import NoPermission from "../common/noPermission";

import TabMenuSnackbar from "../common/tabMenuSnackbar";
import MemoProxy from "../../utility/proxy/manager/memoProxy";


const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#b6b9d6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        color: "#000",
        "&:hover": {
            backgroundColor: "#b6b9d6",
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    }
}));

export default function StoneDetailMemoOut(stoneid) {

    const [stockData, setStockData] = useState([]);
    const [stockLoader, setStockLoader] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [sortModel, setSortModel] = React.useState([]);

    // eslint-disable-next-line
    const [stoneDetailMemoOut, setStoneDetailMemoOut] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.STONEDETAIL_MEMO_OUT
        )
    );

    const handleSearch = (stoneid) => {

        setStockLoader(true);
        MemoProxy.getNoOfMemoOutByStoneId(stoneid.stoneid, (response) => {
            if (response) {
                setStockData(response);
                setStockLoader(false);
            } else {
                setStockLoader(false);
                setErrorSnackbar(true);
            }
        })
    };

    useEffect(() => {
        handleSearch(stoneid);
    }, []);

    const columnName = [
        {
            field: "memoid",
            headerName: "MEMO ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "createdby",
            headerName: "CREATED BY",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: renderCellExpand,
        },
        {
            field: "customer",
            headerName: "CUSTOMER",
            headerAlign: "center",
            align: "center",
            width: 360,
            renderCell: renderCellExpand,
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 350,
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.row.createdat),
        },
    ];

    const rows = stockData;
    const columns = columnName;

    const allowed = Utility.isReadAllowed(stoneDetailMemoOut);

    return (
        !allowed ? <NoPermission /> :
            <div>
                <Container maxWidth="xxl">

                    <TabMenuSnackbar
                        show={successSnackbar}
                        message={'Success'}
                        variant={"success"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Success"}
                        handleClose={() => setSuccessSnackbar(false)}
                    />
                    <TabMenuSnackbar
                        show={errorSnackbar}
                        message={Constants.APiFailErrorMessage}
                        variant={"error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Failed"}
                        handleClose={() => setErrorSnackbar(false)}
                    />
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeightBold,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeightBold,
                            },
                            height: '40vh'
                        }}
                    >
                        <StyledDataGrid
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{ height: "40vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                            slots={{
                                toolbar: GridToolbar,
                            }}
                            rows={rows}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => setSortModel(newModel)}
                            density="compact"
                            columns={columns}
                            getRowId={(row) => row.memoid}
                            loading={stockLoader}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        kapanid: false,
                                        country: false,
                                        quality: false,
                                        shade: false,
                                        repacode: false,
                                        height: false,
                                        width: false,
                                        packetid: false,
                                        crownheight: false,
                                        crownangle: false,
                                        culetsize: false,
                                        culetcondition: false,
                                        depthpercent: false,
                                        gridlemin: false,
                                        intensitylongtitle: false,
                                        laserinscription: false,
                                        note: false,
                                        paviliondepth: false,
                                        open: false,
                                        gridlecondition: false,
                                        gridlepercent: false,
                                        pavilionangle: false,
                                        white: false,

                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />
                        <Box
                            style={{
                                position: "absolute",
                                top: 6,
                                right: "265px",
                                display: "flex",
                            }}
                        >
                            {stockLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch(stoneid)}
                                        color="primary"
                                        fontSize="large"
                                        sx={{ cursor: "pointer", height: "1.8rem", width: "1.8rem" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Container>
            </div>
    );
}
